import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * ParallaxBg — full-bleed section with a background image that translates slower
 * than the scroll, creating depth. Content sits on top.
 */
export function ParallaxBg({ image, children, className = "", overlay = "bg-gradient-to-b from-black/50 via-black/30 to-black/60", minHeight = "min-h-[100vh]", speed = 0.35 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Move image ~30% slower than the viewport
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);

  return (
    <section ref={ref} className={`relative overflow-hidden ${minHeight} ${className}`}>
      <motion.div
        style={{ y }}
        className="absolute inset-0 -top-[15%] -bottom-[15%] will-change-transform"
      >
        <img src={image} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className={`absolute inset-0 ${overlay}`} />
      <div className="relative z-10 h-full">{children}</div>
    </section>
  );
}

/**
 * ParallaxImage — image that translates on scroll within its parent
 */
export function ParallaxImage({ src, alt = "", className = "", speed = 0.2 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}px`, `${speed * 100}px`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale: 1.1 }}
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
      />
    </div>
  );
}

/**
 * FadeUp — reveals children with a fade + slide when they enter viewport
 */
export function FadeUp({ children, delay = 0, className = "", y = 24 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: [0.2, 0.7, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
